import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut,
         sendPasswordResetEmail, UserCredential,
         onAuthStateChanged, User as FirebaseUser, updateProfile, verifyPasswordResetCode,
         confirmPasswordReset, applyActionCode, EmailAuthProvider, reauthenticateWithCredential, 
         checkActionCode,
         signInWithEmailLink} from '@angular/fire/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { User, UserRoleHelper } from '../models/user.model';
import { CustomClaims, CustomClaimsData, ClaimMembershipData, GlobalRoles } from '../models/custom-claims.model';
import { OnboardingService } from './onboarding.service';
import { AuthApiService } from './auth-api.service';
import { OrganizationService } from './organization.service';
import { OrganizationContextService } from './organization-context.service';
import { WebSocketService } from './websocket.service';
import { TicketNotificationService } from './ticket-notification.service';
import { SprayRecordRealtimeService } from './spray-record-realtime.service';
import { ZohoAsapService } from './zoho-asap.service';
import { MembershipTierService } from './membership-tier.service';
import { AnalyticsService } from './analytics.service';
import { AuthMode } from '../models';
import { environment } from 'src/environments/environment';

/**
 * Authentication Service
 * Manages Firebase authentication and custom claims
 * Based on: agent-os/specs/01-user-authentication-sso/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(true);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private organizationService = inject(OrganizationService);
  private organizationContext = inject(OrganizationContextService);
  private websocketService = inject(WebSocketService);
  private ticketNotificationService = inject(TicketNotificationService);
  // Constructed solely so its WebSocket subscriptions are wired at app
  // boot. The service self-subscribes when the socket reaches `authenticated`.
  private sprayRecordRealtimeService = inject(SprayRecordRealtimeService);
  private zohoAsapService = inject(ZohoAsapService);
  private membershipTierService = inject(MembershipTierService);
  private analyticsService = inject(AnalyticsService);

  constructor(
    private auth: Auth,
    private router: Router,
    private onboardingService: OnboardingService,
    private authApi: AuthApiService
  ) {
    this.initAuthListener();
  }

  /**
   * Initialize Firebase auth state listener
   * Also loads organization context when user authenticates
   */
  private initAuthListener(): void {
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        const user = await this.mapFirebaseUserToUser(firebaseUser);
        this.currentUserSubject.next(user);
        this.analyticsService.setUser(user);

        // Load organization context after authentication
        // This ensures X-Organization-Id header is set for API calls
        try {
          await this.organizationService.getMyOrganization();
          // Fetch tier AFTER org context resolves — the status endpoint
          // reads X-Organization-Id, same ordering reason as above. NOT
          // awaited: fetchStatus() never throws (fails closed internally),
          // so there's nothing here to catch, and awaiting it would only
          // add a network round-trip to the login critical path ahead of
          // WebSocket connect/notification init below.
          void this.membershipTierService.fetchStatus();
        } catch (error) {
          // User might not have an organization yet, which is fine
          console.log('No organization found for user (this is normal for new users)');
        }

        // Connect to WebSocket for real-time updates
        this.websocketService.connect();

        // Initialize ticket notifications
        this.ticketNotificationService.initialize();

        // Authenticate with Zoho ASAP widget
        this.zohoAsapService.authenticateUser();
      } else {
        this.currentUserSubject.next(null);
        this.analyticsService.clearUser();
        // Disconnect WebSocket when user logs out
        this.websocketService.disconnect();
        // Logout from Zoho ASAP widget
        this.zohoAsapService.logout();
        // Clear cached tier so it can't leak into a subsequent login
        this.membershipTierService.reset();
      }
      this.loadingSubject.next(false);
    });
  }

  /**
   * Map Firebase user to ADN user model with custom claims
   * Parses compressed custom claims format: {c: <globalClaims>, m: [memberships]}
   */
  private async mapFirebaseUserToUser(firebaseUser: FirebaseUser): Promise<User> {
    const idTokenResult = await firebaseUser.getIdTokenResult();

    // Parse compressed custom claims format from Firebase token
    // Backend sets claims as: {c: <globalClaims>, m: [{o: <orgId>, c: <orgClaims>}]}
    const compressedClaims: CustomClaimsData = {
      c: idTokenResult.claims['c'] as number | undefined,
      m: idTokenResult.claims['m'] as ClaimMembershipData[] | undefined
    };

    // Convert to CustomClaims object for easier handling
    const customClaims = compressedClaims.c !== undefined || compressedClaims.m !== undefined
      ? CustomClaims.fromJSON(compressedClaims)
      : undefined;

    const user: User = {
      uid: firebaseUser.uid,
      email: firebaseUser.email!,
      displayName: firebaseUser.displayName ?? undefined,
      photoURL: firebaseUser.photoURL ?? undefined,
      phoneNumber: firebaseUser.phoneNumber ?? undefined,
      emailVerified: firebaseUser.emailVerified,
      customClaims: customClaims
    };

    return user;
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<User> {
    try {
      const credential: UserCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = await this.mapFirebaseUserToUser(credential.user);

      // Signing in is allowed while unverified, but reaching the app is not —
      // AuthGuard sends anyone with `emailVerified === false` to /verify/email.
      // Kept as a redirect rather than a failed sign-in so the user arrives
      // authenticated and can resend themselves a code without logging in
      // first. Note this diverges from mobile (CropSwipe), which still lets
      // unverified users through.

      // Reset onboarding to show Getting Started on dashboard
      this.onboardingService.resetOnboarding().subscribe();

      // Record the login on the backend (best-effort — must not block sign-in)
      this.authApi.recordLogin().catch(err => console.error('Failed to record login:', err));

      return user;
    } catch (error: any) {
      const wrongPasswordCodes = ['auth/invalid-credential', 'auth/invalid-password'];
      if (wrongPasswordCodes.includes(error.code)) {
        const response = await this.authApi.recordFailedLoginAttempt(email).catch(err => console.error('Failed to record failed login:', err));
        if (response?.locked) error.code = 'auth/too-many-requests';
      }
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string): Promise<User> {
    try {
      const credential: UserCredential = await createUserWithEmailAndPassword(this.auth, email, password);

      const user = await this.mapFirebaseUserToUser(credential.user);

      // Reset onboarding to show Getting Started on dashboard
      this.onboardingService.resetOnboarding().subscribe();

      return user;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out
   */
  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
      this.currentUserSubject.next(null);

      // Clear organization context on logout
      this.organizationContext.clearCurrentOrganization();

      this.router.navigate(['/login']);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Re-authenticate the current user with their password.
   *
   * Required by Firebase before destructive operations (e.g. account
   * deletion). Mirrors the mobile `auth_provider.reauthenticate(password)`
   * call used by `delete_account_screen.dart`.
   *
   * Throws if no user is signed in, or the password is wrong / the request
   * is rate-limited (`handleAuthError` translates Firebase error codes).
   */
  async reauthenticate(password: string): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser || !firebaseUser.email) {
      throw new Error('No user is currently signed in.');
    }

    try {
      const credential = EmailAuthProvider.credential(firebaseUser.email, password);
      await reauthenticateWithCredential(firebaseUser, credential);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Apply a Firebase out-of-band action code. Used by the email-action
   * handler for the `verifyEmail` and `recoverEmail` modes (password
   * reset uses the dedicated verify/confirm pair above).
   *
   * On success the action is committed server-side. If the current user
   * is signed in we refresh their token so the local `emailVerified`
   * flag reflects the change without requiring a re-login.
   */
  async applyActionCode(oobCode: string): Promise<void> {
    try {
      await applyActionCode(this.auth, oobCode);

      // Reflect the freshly-verified state locally if this is the
      // signed-in user (e.g. they just signed up and clicked the link in
      // the same browser session).
      const firebaseUser = this.auth.currentUser;
      if (firebaseUser) {
        await firebaseUser.reload();
        const user = await this.mapFirebaseUserToUser(firebaseUser);
        this.currentUserSubject.next(user);
      }
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No user is currently signed in.');
    }

    try {
      await this.authApi.resendVerificationEmail();
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Update user profile (displayName, photoURL)
   */
  async updateUserProfile(displayName?: string, photoURL?: string): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No user is currently signed in.');
    }

    try {
      const updates: { displayName?: string; photoURL?: string } = {};
      if (displayName !== undefined) updates.displayName = displayName;
      if (photoURL !== undefined) updates.photoURL = photoURL;

      await updateProfile(firebaseUser, updates);

      // Update local user state
      const user = await this.mapFirebaseUserToUser(firebaseUser);
      this.currentUserSubject.next(user);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Refresh ID token to get updated custom claims
   */
  async refreshToken(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) return;

    try {
      await firebaseUser.getIdToken(true); // Force refresh
      const user = await this.mapFirebaseUserToUser(firebaseUser);
      this.currentUserSubject.next(user);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Re-reads the signed-in user from Firebase and re-emits them.
   *
   * `emailVerified` rides on the local user object, so it goes stale when the
   * address is verified somewhere this tab can't see — the emailed link opened
   * in a different browser, or on a phone. Without a reload the gate would
   * keep rejecting someone who has, in fact, already verified.
   *
   * Returns the refreshed user, or null when nobody is signed in.
   */
  async reloadCurrentUser(): Promise<User | null> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) return null;

    try {
      await firebaseUser.reload();
      const user = await this.mapFirebaseUserToUser(firebaseUser);
      this.currentUserSubject.next(user);
      return user;
    } catch (error: any) {
      // Best-effort — a failed reload just leaves the existing state in place.
      console.error('Failed to reload current user:', error);
      return this.currentUserSubject.value;
    }
  }

  /**
   * Get current user (synchronous)
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  /**
   * Refresh the current session after custom claims have been updated server-side.
   * Forces a token refresh, re-maps the user with updated claims, and re-loads organization context.
   * Call this after accepting an invitation or any operation that updates Firebase custom claims.
   */
  async refreshSession(): Promise<void> {
    // Refresh token and re-map user using the centralized implementation
    await this.refreshToken();

    // Re-load organization context with updated membership
    try {
      await this.organizationService.getMyOrganization();
    } catch (error) {
      console.log('No organization found for user after session refresh');
    }
  }

  /**
   * Check if user has a specific global role (Admin, SuperAdmin, Customer)
   */
  hasRole(role: GlobalRoles): boolean {
    const user = this.getCurrentUser();
    return user ? UserRoleHelper.hasRole(user, role) : false;
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    return user ? UserRoleHelper.hasPermission(user, permission) : false;
  }

  /**
   * Handle Firebase auth errors
   */
  handleAuthError(error: any): Error {
    let message = 'An unknown error occurred.';

    switch (error.code) {
      case 'auth/invalid-credential':
        message = 'Invalid email or password';
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address.';
        break;
      case 'auth/user-disabled':
        message = 'This account has been disabled.';
        break;
      case 'auth/user-not-found':
        message = 'No account found with this email.';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password.';
        break;
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists.';
        break;
      case 'auth/weak-password':
        message = 'Password must be at least 6 characters.';
        break;
      case 'auth/too-many-requests':
        message = 'Too many failed attempts. Please try again later.';
        break;
      case 'auth/network-request-failed':
        message = 'Network error. Please check your connection.';
        break;
      case 'auth/invalid-action-code':
        message = 'This link is invalid or has expired. Request a new one.';
        break;
      default:
        message = error.message || message;
    }
    
    return new Error(message);
  }

  async verifyPasswordResetCode(oobCode: string): Promise<string> {
    try {
      return await verifyPasswordResetCode(this.auth, oobCode); // returns email
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }
  
  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, oobCode, newPassword);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async loginWithCode(oobCode: string, email: string): Promise<void> {
    const { apiKey, authDomain } = environment.firebase;
    const link = `https://${authDomain}/__/auth/action?mode=signIn&oobCode=${oobCode}&apiKey=${apiKey}`;
    
    try {
      await signInWithEmailLink(this.auth, email, link);
      this.authApi.recordLogin().catch(err => console.error('Failed to record login:', err));
    } catch (error) {
      throw this.handleAuthError(error)
    }
  }

  async verifyEmail(oobCode: string): Promise<void> {
    try {
      const actionCodeInfo = await checkActionCode(this.auth, oobCode)

      // A reset-password or recover-email code would otherwise fall through
      // here and the caller would report the address as verified.
      if (actionCodeInfo.operation !== 'VERIFY_EMAIL' || !actionCodeInfo.data.email) {
        throw { code: 'auth/invalid-action-code' };
      }

      await this.applyActionCode(oobCode);
      await this.authApi.verifyEmail(actionCodeInfo.data.email);
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Verifies an address from the six-digit code in the email instead of the
   * link, for users who never left the browser to click it.
   *
   * The code is only a lookup key — the Hub trades it for the `oobCode` from
   * the same Firebase action the link carries, so verification itself still
   * runs through the identical path.
   *
   * The Hub answers every rejected code with a 400, so a bad or expired code
   * arrives here as a thrown `HttpErrorResponse`, not a falsy return.
   */
  async verifyEmailWithCode(email: string, code: string): Promise<void> {
    const response = await this.authApi.verifyAuthEmailCode(email, code, AuthMode.verify);

    if (!response.data) {
      throw new Error('That code is invalid or has expired.');
    }

    await this.verifyEmail(response.data);
  }
}
